import type { Adb } from "@yume-chan/adb";
import { DeviceToolError } from "../errors";
import type { DeviceInfo, UsbProtocolService } from "../types";
import { parsePackageList, type AdbApplication } from "./AdbApplications";
import { AdbWebUsbAdapter } from "./AdbWebUsbAdapter";

export interface AdbInstallOptions { replace: boolean; downgrade: boolean; grant: boolean }

export class AdbService implements UsbProtocolService {
  readonly adapter = new AdbWebUsbAdapter();

  get adb(): Adb | undefined { return this.adapter.client; }

  async connect(): Promise<void> { await this.adapter.connect(); }
  async connectAuthorizedDevice(device: USBDevice): Promise<void> { await this.adapter.connectAuthorizedDevice(device); }
  async disconnect(): Promise<void> { await this.adapter.disconnect(); }
  isConnected(): boolean { return this.adapter.isConnected(); }

  async getDeviceInfo(): Promise<DeviceInfo> {
    const device = this.adapter.rawDevice;
    if (!this.adb) throw new DeviceToolError("DEVICE_NOT_SELECTED");
    if (!device) throw new DeviceToolError("DEVICE_NOT_SELECTED");
    return { protocol: "ADB", vendorId: device.vendorId, productId: device.productId, manufacturer: device.manufacturerName, product: device.productName, serial: this.adb.serial, usbVersion: `${device.usbVersionMajor}.${device.usbVersionMinor}.${device.usbVersionSubminor}`, interfaceClaimed: true, connectionType: "USB" };
  }

  getProperties(): Promise<Record<string, string>> { return this.adapter.getProperties(); }
  async listApplications(signal?: AbortSignal): Promise<AdbApplication[]> {
    const [all, system, user, disabled] = await Promise.all([
      this.shell("pm list packages -f -U -u", signal),
      this.shell("pm list packages -s", signal),
      this.shell("pm list packages -3", signal),
      this.shell("pm list packages -d", signal),
    ]);
    const result = [all, system, user, disabled];
    const failed = result.find((item) => item.exitCode !== 0);
    if (failed) throw new DeviceToolError("ADB_COMMAND_FAILED", failed.stderr || failed.stdout || "Không thể đọc danh sách ứng dụng.");
    return parsePackageList(all.stdout, system.stdout, user.stdout, disabled.stdout);
  }
  enableApplication(packageName: string, signal?: AbortSignal): Promise<{ stdout: string; stderr: string; exitCode: number }> { return this.runPackageCommand(`pm enable --user 0 ${safePackageName(packageName)}`, signal); }
  disableApplication(packageName: string, signal?: AbortSignal): Promise<{ stdout: string; stderr: string; exitCode: number }> { return this.runPackageCommand(`pm disable-user --user 0 ${safePackageName(packageName)}`, signal); }
  uninstallApplication(packageName: string, signal?: AbortSignal): Promise<{ stdout: string; stderr: string; exitCode: number }> { return this.runPackageCommand(`pm uninstall --user 0 ${safePackageName(packageName)}`, signal); }
  clearApplicationData(packageName: string, signal?: AbortSignal): Promise<{ stdout: string; stderr: string; exitCode: number }> { return this.runPackageCommand(`pm clear --user 0 ${safePackageName(packageName)}`, signal); }
  shell(command: string, signal?: AbortSignal): Promise<{ stdout: string; stderr: string; exitCode: number }> { return this.adapter.shell(command, signal); }
  reboot(mode?: "recovery" | "bootloader" | "fastboot" | "sideload" | ""): Promise<string> { return this.adapter.reboot(mode); }
  installApk(file: File, options: AdbInstallOptions, signal?: AbortSignal) { return this.adapter.installApk(file, options, signal); }
  push(file: File, remotePath: string, onProgress?: (completed: number) => void, signal?: AbortSignal) { return this.adapter.push(file, remotePath, onProgress, signal); }
  pull(remotePath: string, suggestedName: string, onProgress?: (completed: number) => void, signal?: AbortSignal) { return this.adapter.pull(remotePath, suggestedName, onProgress, signal); }
  sideload(file: File, onProgress?: Parameters<AdbWebUsbAdapter["sideload"]>[1], signal?: AbortSignal) { return this.adapter.sideload(file, onProgress, signal); }
  async cancelCurrentOperation(): Promise<void> { await this.adapter.cancelCurrentOperation(); }

  private async runPackageCommand(command: string, signal?: AbortSignal): Promise<{ stdout: string; stderr: string; exitCode: number }> {
    const result = await this.shell(command, signal);
    if (result.exitCode !== 0) throw new DeviceToolError("ADB_COMMAND_FAILED", `${result.stderr || result.stdout} (exit ${result.exitCode})`);
    return result;
  }
}

function safePackageName(packageName: string): string {
  if (!/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(packageName)) throw new DeviceToolError("ADB_COMMAND_FAILED", "Tên package không hợp lệ.");
  return packageName;
}
